import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaCalendarTimes, FaFileAlt, FaSort, FaSortUp, FaSortDown, FaBuilding, FaMapMarkerAlt } from 'react-icons/fa';
import axios from 'axios';
import swal from 'sweetalert';
import { formatDate, formatDate01 } from '../../helper/helper';
import { documentUrl } from '../../helper/config';
import { adminTable, formatLokasiList } from '../../component/Admin/adminTable';
import Loading from '../../component/Loading';
import AttendanceCalendar from '../../component/Panel/AttendanceCalendar';
import AttendanceDetailModal from '../../component/Panel/AttendanceDetailModal';

const DetailUser = ({ data }) => {
    const { id_user } = useParams();
    const navigate = useNavigate();
    const [dateCurrent, setDateCurrent] = useState(new Date());
    const [dataAbsensi, setDataAbsensi] = useState(null);
    const [dataIzinCuti, setDataIzinCuti] = useState([]);
    const [userInfo, setUserInfo] = useState(null);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [activeStartDate, setActiveStartDate] = useState(new Date());
    const [hariLibur, setHariLibur] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    // Sort states for izin/cuti
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'

    const header = {
        withCredentials: true
    };

    // Get user info
    const getUserInfo = async () => {
        try {
            const [usersRes, lokasiRes] = await Promise.all([
                axios.get(`/api/users`, header),
                axios.get(`/api/lokasi/user/${id_user}`, { withCredentials: true }),
            ]);
            const user = usersRes.data.items.find(u => u.id_user === parseInt(id_user));
            if (user) {
                const lokasiNames = (lokasiRes.data || []).map((l) => l.nama);
                setUserInfo({ ...user, lokasi_names: lokasiNames });
            }
        } catch (error) {
            console.error("Error fetching user info:", error);
        }
    };

    // Get attendance data for calendar
    const getDataAbsensi = async (refDate = dateCurrent) => {
        try {
            // Format date to YYYY-MM-DD for month endpoint (backend expects YYYY-MM-DD format)
            const monthStr = formatDate01(refDate);
            const response = await axios.get(`/api/absensi/${id_user}/${monthStr}`, header);
            setDataAbsensi(response.data || []);
        } catch (error) {
            console.error("Error fetching attendance data:", error);
            setDataAbsensi([]);
        }
    };

    const getHariLibur = async (refDate = dateCurrent) => {
        try {
            const year = refDate.getFullYear();
            const month = refDate.getMonth() + 1;
            const response = await axios.get(`/api/hari-libur/bulan/${year}/${month}`, {
                withCredentials: true,
            });
            setHariLibur(response.data || []);
        } catch (error) {
            console.error("Error fetching hari libur:", error);
            setHariLibur([]);
        }
    };

    // Get izin and cuti history
    const getDataIzinCuti = async () => {
        try {
            const response = await axios.get(`/api/absensi/${id_user}`, header);
            console.log("Raw absensi data:", response.data);

            // Filter only izin and cuti, and ensure we have the required fields
            const izinCuti = response.data
                .filter(item => {
                    const status = item.status || item.a_status;
                    return (status === 'izin' || status === 'cuti') && status !== null && status !== undefined;
                })
                .map(item => ({
                    tanggal: item.tanggal || item.a_tanggal,
                    status: item.status || item.a_status,
                    keterangan: item.keterangan || item.a_keterangan || '',
                    document: item.document || item.a_document || null,
                    idAbsensi: item.idAbsensi || item.id_absensi || item.a_id_absensi
                }));

            console.log("Filtered izin/cuti data:", izinCuti);
            setDataIzinCuti(izinCuti);
        } catch (error) {
            console.error("Error fetching izin/cuti data:", error);
            setDataIzinCuti([]);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            await Promise.all([
                getUserInfo(),
                getDataAbsensi(dateCurrent),
                getHariLibur(dateCurrent),
                getDataIzinCuti()
            ]);
            setLoading(false);
        };
        fetchData();
    }, [id_user]);

    useEffect(() => {
        getDataAbsensi(dateCurrent);
        getHariLibur(dateCurrent);
    }, [dateCurrent]);

    const handleDateClick = (date) => {
        const dateStr = formatDate01(date);
        const filteredData = dataAbsensi.filter(item => {
            return formatDate01(item.tanggal) === dateStr;
        });
        if (filteredData.length > 0) {
            setModalData(filteredData);
            setModalIsOpen(true);
        } else {
            setModalData(null);
            setModalIsOpen(false);
        }
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setModalData(null);
    };

    const authConfig = {
        withCredentials: true,
        withCredentials: true,
    };

    const handleDeleteMasuk = async (item) => {
        const id = item.idAbsensi || item.id_absensi;
        const ok = await swal({
            title: 'Hapus Absen Masuk?',
            text: 'Foto dan waktu masuk akan dihapus.',
            icon: 'warning',
            buttons: ['Batal', 'Hapus'],
            dangerMode: true,
        });
        if (!ok) return;
        try {
            await axios.delete(`/api/absensi/${id}/masuk`, authConfig);
            await swal('Berhasil', 'Absen masuk dihapus', 'success');
            await getDataAbsensi();
            closeModal();
        } catch (error) {
            swal('Gagal', error.response?.data?.message || 'Gagal menghapus absen masuk', 'error');
        }
    };

    const handleDeletePulang = async (item) => {
        const id = item.idAbsensi || item.id_absensi;
        const ok = await swal({
            title: 'Hapus Absen Pulang?',
            text: 'Foto dan waktu pulang akan dihapus.',
            icon: 'warning',
            buttons: ['Batal', 'Hapus'],
            dangerMode: true,
        });
        if (!ok) return;
        try {
            await axios.delete(`/api/absensi/${id}/pulang`, authConfig);
            await swal('Berhasil', 'Absen pulang dihapus', 'success');
            await getDataAbsensi();
            closeModal();
        } catch (error) {
            swal('Gagal', error.response?.data?.message || 'Gagal menghapus absen pulang', 'error');
        }
    };

    const handleDeleteAll = async (item) => {
        const id = item.idAbsensi || item.id_absensi;
        const ok = await swal({
            title: 'Hapus Seluruh Absensi Hari Ini?',
            text: 'Data masuk dan pulang akan dihapus permanen.',
            icon: 'warning',
            buttons: ['Batal', 'Hapus Semua'],
            dangerMode: true,
        });
        if (!ok) return;
        try {
            await axios.delete(`/api/absensi/${id}`, authConfig);
            await swal('Berhasil', 'Data absensi dihapus', 'success');
            await getDataAbsensi();
            closeModal();
        } catch (error) {
            swal('Gagal', error.response?.data?.message || 'Gagal menghapus data absensi', 'error');
        }
    };

    const handleActiveStartDateChange = ({ activeStartDate: start }) => {
        if (!start) return;
        setActiveStartDate(start);
        setDateCurrent(new Date(start.getFullYear(), start.getMonth(), 1));
    };

    const handleExportExcel = async () => {
        if (!id_user || isNaN(Number(id_user))) {
            swal('Error', 'ID user tidak valid', 'error');
            return;
        }

        const y = dateCurrent.getFullYear();
        const m = String(dateCurrent.getMonth() + 1).padStart(2, '0');
        const monthStr = `${y}-${m}`;

        try {
            setExporting(true);
            const response = await axios.get(`/api/absensi/export/${id_user}?month=${monthStr}-01`, {
                withCredentials: true,
                responseType: 'blob',
                withCredentials: true,
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Rekap_Absensi_${monthStr}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            swal('Success', 'File Excel berhasil diunduh', 'success');
        } catch (error) {
            console.error('Error exporting Excel:', error);
            swal('Error', 'Gagal mengunduh file Excel', 'error');
        } finally {
            setExporting(false);
        }
    };

    // Handle sort for izin/cuti
    const handleSort = (field) => {
        if (sortField === field) {
            // Toggle direction if same field
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    // Get sort icon
    const getSortIcon = (field) => {
        if (sortField !== field) {
            return <FaSort className="ml-1 text-gray-400" />;
        }
        return sortDirection === 'asc'
            ? <FaSortUp className="ml-1 text-white" />
            : <FaSortDown className="ml-1 text-white" />;
    };

    // Sorted izin/cuti data
    const sortedIzinCuti = useMemo(() => {
        if (!sortField) return dataIzinCuti;

        return [...dataIzinCuti].sort((a, b) => {
            let aValue, bValue;

            switch (sortField) {
                case 'tanggal':
                    aValue = new Date(a.tanggal);
                    bValue = new Date(b.tanggal);
                    break;
                case 'status':
                    aValue = a.status || '';
                    bValue = b.status || '';
                    break;
                case 'keterangan':
                    aValue = a.keterangan || '';
                    bValue = b.keterangan || '';
                    break;
                default:
                    return 0;
            }

            if (sortField === 'tanggal') {
                return sortDirection === 'asc'
                    ? (aValue - bValue)
                    : (bValue - aValue);
            }

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return sortDirection === 'asc'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }

            return sortDirection === 'asc'
                ? (aValue > bValue ? 1 : -1)
                : (aValue < bValue ? 1 : -1);
        });
    }, [dataIzinCuti, sortField, sortDirection]);

    if (loading) {
        return <Loading />;
    }

    return (
        <div className='min-h-[calc(100vh-100px)] bg-white rounded-lg p-4 md:p-6'>
            {/* Header */}
            <div className='flex items-center justify-between mb-6'>
                <div className='flex items-center gap-4'>
                    <button
                        onClick={() => navigate('/admin/users')}
                        className='p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors'
                        title="Kembali"
                    >
                        <FaArrowLeft className='text-xl' />
                    </button>
                    <div>
                        <h1 className='font-semibold text-2xl text-gray-800'>
                            Detail Pegawai
                        </h1>
                        {userInfo && (
                            <p className='text-sm text-gray-600 mt-1'>
                                {userInfo.nama} — {userInfo.username}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {userInfo && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1 flex items-center gap-1"><FaBuilding /> Devisi</p>
                        <p className="font-semibold text-sm text-gray-800">{userInfo.id_devisi?.nama_divisi || '-'}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 md:col-span-2">
                        <p className="text-xs text-gray-500 mb-1 flex items-center gap-1"><FaMapMarkerAlt /> Lokasi Absensi</p>
                        <p className="font-semibold text-sm text-gray-800">{formatLokasiList(userInfo.lokasi_names)}</p>
                    </div>
                </div>
            )}

            {/* Calendar Section */}
            <div className='flex flex-col lg:flex-row gap-6'>
                <div className='mb-8 w-full lg:max-w-md'>
                    <h2 className='font-semibold text-xl mb-4 text-gray-800 flex items-center gap-2'>
                        <FaCalendarTimes className='text-[#1B3C33]' />
                        History Absensi
                    </h2>
                    <div className='kalender px-2 pb-4 w-full bg-white'>
                        <AttendanceCalendar
                            dataAbsensi={dataAbsensi || []}
                            hariLibur={hariLibur}
                            activeStartDate={activeStartDate}
                            onActiveStartDateChange={handleActiveStartDateChange}
                            onClickDay={handleDateClick}
                            onExportExcel={handleExportExcel}
                            exporting={exporting}
                        />
                    </div>
                </div>

                {/* Izin and Cuti History Section */}
                <div className='mb-8'>
                    <h2 className='font-semibold text-xl mb-4 text-gray-800 flex items-center gap-2'>
                        <FaFileAlt className='text-[#1B3C33]' />
                        History Izin & Cuti
                    </h2>
                    {dataIzinCuti.length > 0 ? (
                        <div className={adminTable.card}>
                            <div className={adminTable.scroll}>
                                <table className={adminTable.table}>
                                    <thead className={adminTable.thead}>
                                        <tr>
                                            <th className={adminTable.thSortable} onClick={() => handleSort('tanggal')}>
                                                <div className="flex items-center">Tanggal {getSortIcon('tanggal')}</div>
                                            </th>
                                            <th className={adminTable.thSortable} onClick={() => handleSort('status')}>
                                                <div className="flex items-center">Status {getSortIcon('status')}</div>
                                            </th>
                                            <th className={adminTable.thSortable} onClick={() => handleSort('keterangan')}>
                                                <div className="flex items-center">Keterangan {getSortIcon('keterangan')}</div>
                                            </th>
                                            <th className={adminTable.thCenter}>Dokumen</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedIzinCuti.map((item, index) => (
                                            <tr key={index} className={adminTable.tr}>
                                                <td className={adminTable.td}>
                                                    {item.tanggal ? formatDate(item.tanggal) : '-'}
                                                </td>
                                                <td className={adminTable.td}>
                                                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${item.status === 'izin'
                                                        ? 'bg-orange-100 text-orange-800'
                                                        : 'bg-blue-100 text-blue-800'
                                                        }`}>
                                                        {item.status?.toUpperCase() || '-'}
                                                    </span>
                                                </td>
                                                <td className={adminTable.td}>
                                                    {item.keterangan || '-'}
                                                </td>
                                                <td className={adminTable.td}>
                                                    {item.document ? (
                                                        <a
                                                            href={documentUrl(item.document)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className='text-blue-600 hover:text-blue-800 underline'
                                                        >
                                                            Lihat Dokumen
                                                        </a>
                                                    ) : (
                                                        <span className='text-gray-400'>-</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : (
                        <div className='bg-white rounded-lg border border-gray-200 p-8 text-center'>
                            <FaCalendarTimes className='mx-auto text-4xl mb-2 text-gray-400' />
                            <p className='text-gray-500'>Belum ada data izin atau cuti</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal for Attendance Detail */}
            <AttendanceDetailModal
                isOpen={modalIsOpen}
                onClose={closeModal}
                items={modalData}
                onDeleteMasuk={handleDeleteMasuk}
                onDeletePulang={handleDeletePulang}
                onDeleteAll={handleDeleteAll}
            />
        </div>
    );
};

export default DetailUser;

